import { EditTable, EditTableSortColumn, IEditController } from "../../../shared/components/edit-table/edit-table";
import { TableRenderer, TableRenderType } from "../../../shared/components/edit-table/table-renderer";
import { MonthYearFilter, MonthYearFilterOptions } from "../../../shared/components/filter/filter-types/MonthYearFilter";
import { FilterDataType, FilterTab, MultiselectFilter, MultiselectFilterOptions } from "../../../shared/components/filter/filter.objects";
import { JsonItem } from "../../../shared/entities/pick-list/pick-list.entities";
import { getDisplayDate, getMonthYearDisplay } from "../../../shared/scripts/date-functions";
import { TableColumnGroup } from "../../../shared/scripts/table-column-visibility";
import { CabHubCompletedSurveyRow } from "./cab-hub-tracking.entities";

export class CompletedSurveyTable {
    table: EditTable;
    tableRenderer: TableRenderer;

    constructor(type: CompletedSurveyTableType) {
        let showCount = true;
        if (type == CompletedSurveyTableType.Connection) showCount = false;

        let sortColumn = 4;
        if (type == CompletedSurveyTableType.CAB) sortColumn = 2;
        else if (type == CompletedSurveyTableType.HubRedacted || type == CompletedSurveyTableType.Hub) sortColumn = 3;

        let filterTabs: FilterTab[] = [];
        if (type == CompletedSurveyTableType.Admin) {
            filterTabs = [
                new FilterTab("completed-survey-filter-tab", "", "",
                    [
                        new MultiselectFilter("survey-type-filter", "Survey Type", "SurveyType", new MultiselectFilterOptions(false, null, FilterDataType.String, false, false)),
                        new MonthYearFilter("reporting-period-filter", "Reporting Period", "ReportingMonth", "ReportingYear", new MonthYearFilterOptions(true, false)),
                        new MultiselectFilter("cab-member-filter", "CAB Member", "CabMemberName", new MultiselectFilterOptions(false, null, FilterDataType.String, true, false)),
                        new MultiselectFilter("hub-filter", "Hub", "HubShortName", new MultiselectFilterOptions(false, null, FilterDataType.String, true, false)),
                    ]
                )
            ]
        }
        else if (type == CompletedSurveyTableType.Hub || type == CompletedSurveyTableType.HubRedacted) {
            filterTabs = [
                new FilterTab("completed-survey-filter-tab", "", "",
                    [
                        new MonthYearFilter("reporting-period-filter", "Reporting Period", "ReportingMonth", "ReportingYear", new MonthYearFilterOptions(true, false)),
                        new MultiselectFilter("cab-member-filter", "CAB Member", "CabMemberName", new MultiselectFilterOptions(false, null, FilterDataType.String, true, false)),
                    ]
                )
            ]
        }

        let isEdit = false;
        let editController = null;
        if (type == CompletedSurveyTableType.Admin) {
            editController = new CompletedSurveyController();
            isEdit = true;
        }

        let columns: any[] = [];
        if (type == CompletedSurveyTableType.CAB) {
            let cabColumns = [
                {
                    data: null, title: "Reporting Period", className: "text-left font-size12 nowrap",
                    render: (row: CabHubCompletedSurveyRow, type: string): string => {
                        const display = getMonthYearDisplay(row.ReportingMonth, row.ReportingYear);
                        return type === "display" ? display : `${row.ReportingYear}-${row.ReportingMonth}`;
                    }
                },
                { data: "HubShortName", title: "Hub", className: "text-left font-size12 nowrap" },
                {
                    data: "SubmittedDate", title: "Submitted", className: "text-left font-size12 nowrap",
                    render: (value: string, type: string): string => {
                        if (!value) {
                            return "";
                        }

                        return type === "display" ? getDisplayDate(new Date(value)) : value;
                    }
                },
            ];

            columns.push(...cabColumns);
        }
        else if (type == CompletedSurveyTableType.HubRedacted || type == CompletedSurveyTableType.Hub) {
            let hubRedactedCols = [
                {
                    data: null, title: "Reporting Period", className: "text-left font-size12 nowrap",
                    render: (row: CabHubCompletedSurveyRow, type: string): string => {
                        const display = getMonthYearDisplay(row.ReportingMonth, row.ReportingYear);
                        return type === "display" ? display : `${row.ReportingYear}-${row.ReportingMonth}`;
                    }
                },
                { data: "CabMemberName", title: "CAB Member", className: "text-left font-size12 nowrap" },
                { data: "HubShortName", title: "Hub", className: "text-left font-size12 nowrap" },
                {
                    data: "SubmittedDate", title: "Submitted", className: "text-left font-size12 nowrap",
                    render: (value: string, type: string): string => {
                        if (!value) {
                            return "";
                        }

                        return type === "display" ? getDisplayDate(new Date(value)) : value;
                    }
                }
            ];

            columns.push(...hubRedactedCols);
        }
        else if (type == CompletedSurveyTableType.Admin || type == CompletedSurveyTableType.Connection) {
            let adminCols = [
                { data: "SurveyType", title: "Survey Type", className: "text-left font-size12 nowrap" },
                {
                    data: null, title: "Reporting Period", className: "text-left font-size12 nowrap",
                    render: (row: CabHubCompletedSurveyRow, type: string): string => {
                        const display = getMonthYearDisplay(row.ReportingMonth, row.ReportingYear);
                        return type === "display" ? display : `${row.ReportingYear}-${row.ReportingMonth}`;
                    }
                },
                { data: "CabMemberName", title: "CAB Member", className: "text-left font-size12 nowrap" },
                { data: "HubShortName", title: "Hub", className: "text-left font-size12 nowrap" },
                {
                    data: "SubmittedDate", title: "Submitted", className: "text-left font-size12 nowrap",
                    render: (value: string, type: string): string => {
                        if (!value) {
                            return "";
                        }

                        return type === "display" ? getDisplayDate(new Date(value)) : value;
                    }
                },
            ];

            columns.push(...adminCols);
        }

        if (type != CompletedSurveyTableType.HubRedacted) {
            let surveyColumns = [
                {
                    data: "MetThisMonth", title: "Met This Month", className: "text-center font-size12 nowrap",
                    render: (value: boolean, type: string): string => {
                        const text = value ? "Yes" : "No";

                        if (type !== "display") {
                            return text;
                        }

                        return `<span class="${value ? "cab-edit-table-active-pill" : "cab-detail-table-inactive-pill"}">${text}</span>`;
                    }
                },
                {
                    data: "SupportRequested", title: "Support Requested", className: "text-center font-size12 nowrap",
                    render: (value: boolean, type: string): string => {
                        const text = value ? "Yes" : "No";

                        if (type !== "display") {
                            return text;
                        }

                        return `<span class="${value == false ? "cab-edit-table-active-pill" : "cab-detail-table-inactive-pill"}">${text}</span>`;
                    }
                },
                {
                    data: "SurveyId", title: "View", className: "text-center font-size12 nowrap no-export", orderable: false,
                    render: (value: number, type: string, row: CabHubCompletedSurveyRow): string => {
                        if (type !== "display") {
                            return `${value ?? ""}`;
                        }
                        //need to open modal view of survey on hub and cab
                        return `
                            <button type="button" class="btn btn-sm font-size14 js-view-completed-survey" data-survey-id="${value}" data-survey-type="${row.SurveyType}">
                                View
                            </button>
                        `;
                    }
                },
                { data: "SatisfactionRatingId", title: "Satisfaction", className: "text-left font-size12 advanced-survey-column nowrap", render: (data: number): string => { return this.tableRenderer.renderDisplay(data, TableRenderType.Number, "SatisfactionRating"); } },
                { data: "NotMetExplanation", title: "Not Met Explanation", className: "text-left font-size12 nowrap advanced-survey-column" },
                { data: "HoursWorked", title: "Hours Worked", className: "text-left font-size12 nowrap advanced-survey-column" },
                { data: "AdditionalHoursCompensationRate", title: "Additional Compensation", className: "text-left font-size12 nowrap advanced-survey-column" },
                { data: "AnticipatedHoursNextMonthId", title: "Anticipated 2+ Hours Next Month", className: "text-left font-size12 nowrap advanced-survey-column", render: (data) => { return this.tableRenderer.renderDisplay(data, TableRenderType.Number, "YesNoNotSure") } },
                {
                    data: "ConsultationTypeIds", title: "Consultation Types", className: "text-left font-size12 nowrap advanced-survey-column",
                    render: (value: string, type: string, row: CabHubCompletedSurveyRow): string => {
                        const pickListId = row.SurveyType === "CAB" ? "CabConsultationType" : "HubConsultationType";
                        return this.tableRenderer.renderDisplay(value, TableRenderType.PipePathSeparatedIds, pickListId);
                    }
                },
                { data: "OtherConsultationType", title: "Other Consultation Type", className: "text-left nowrap font-size12 advanced-survey-column" },
                { data: "WorkDescription", title: "Work Description", className: "text-left font-size12 nowrap advanced-survey-column" },
                { data: "SatisfactionExplanation", title: "Satisfaction Explanation", className: "text-left font-size12 nowrap advanced-survey-column" },
                { data: "SupportRequestExplanation", title: "Support Request Explanation", className: "text-left nowrap font-size12 col-lg advanced-survey-column" },
                { data: "TrainingNeededId", title: "Training Needed", className: "text-left font-size12 nowrap advanced-survey-column", render: (data) => { return this.tableRenderer.renderDisplay(data, TableRenderType.Number, "YesNoNotSure") } },
                { data: "TrainingDescription", title: "Training Description", className: "text-left font-size12 nowrap col-lg advanced-survey-column" },
                { data: "SubmittedByName", title: "Submitted By", className: "text-left font-size12 nowrap advanced-survey-column" }
            ];

            columns.push(...surveyColumns);
        }   

        this.table = new EditTable({
            tableId: "completed-survey-table",
            showCount: showCount,
            columns: columns,
            filterTabs: filterTabs,
            tableColumnGroups: [
                new TableColumnGroup("Standard Columns", true, [], ""),
                new TableColumnGroup("Advanced Survey Details", false, [], "advanced-survey-column")
            ],
            sortOrder: [new EditTableSortColumn(sortColumn, "desc")],
            itemDisplayName: "Surveys",
            editController: editController,
            isEditable: isEdit,
            propertyIdName: "SurveyId"
        })
    }

    generateHtml(): string {
        return this.table.generateHtml();
    }

    initialize(items: any[], pickLists: JsonItem[]): void {
        this.tableRenderer = new TableRenderer(pickLists);
        this.table.initialize(items, pickLists);
    }

    resizeTable(): void {
        this.table.resizeTable();
    }
}

export enum CompletedSurveyTableType {
    CAB,
    Hub,
    HubRedacted,
    Admin,
    Connection
}

class CompletedSurveyController implements IEditController {
    constructor() { }

    public async saveItems() { return; }
    public getEditMenu(): string {
        return `
            <a class="dropdown-item dropdown-edit-survey-option">
                <i class="fa-solid fa-pen-to-square dropdown-icon pr-2"></i> Edit Survey
            </a>
        `;
    }
}